import { useQuery } from '@tanstack/react-query';
import { fetchAllPaginated } from '@/lib/supabase-paginate';
import {
  computeTimeprisRanking,
  computeBfRanking,
  type ProjectInput,
  type TimeprisRankEntry,
  type BfRankEntry,
} from '@/lib/top-projects-calc';

interface RawProject {
  id: string;
  name: string;
  project_code: string | null;
  total_amount: number | null;
  bf_actual: number | null;
  project_status: string | null;
  production_lead_id: string | null;
  customer: { name: string } | null;
}

interface RawAssignment {
  hours: number | null;
  budget_lines: { project_id: string } | null;
}

interface RawTimeEntry {
  project_id: string | null;
  duration_seconds: number | null;
}

interface RawOwner {
  project_id: string;
  user_id: string | null;
}

interface RawUser {
  id: string;
  full_name: string;
}

export interface TopProjectsData {
  timeprisRanking: TimeprisRankEntry[];
  bfRanking: BfRankEntry[];
}

export function useTopProjects(includeCredits = false) {
  return useQuery({
    queryKey: ['top-projects', { includeCredits }],
    queryFn: async (): Promise<TopProjectsData> => {
      const [projects, assignments, timeEntries, owners, users] = await Promise.all([
        fetchAllPaginated<RawProject>(
          'projects',
          'id, name, project_code, total_amount, bf_actual, project_status, production_lead_id, customer:customer_id(name)',
          (q) => q.is('deleted_at', null),
        ),
        fetchAllPaginated<RawAssignment>(
          'budget_line_assignments',
          'hours, budget_lines!inner(project_id, cost_type)',
          (q) => q.neq('budget_lines.cost_type', 'external'),
        ),
        fetchAllPaginated<RawTimeEntry>(
          'time_entries',
          'project_id, duration_seconds',
          (q) => q.is('deleted_at', null),
        ),
        includeCredits
          ? fetchAllPaginated<RawOwner>('project_owners', 'project_id, user_id')
          : Promise.resolve([]),
        includeCredits
          ? fetchAllPaginated<RawUser>('users_safe', 'id, full_name')
          : Promise.resolve([]),
      ]);

      const userNameById = new Map(users.map((user) => [user.id, user.full_name]));
      const salesOwnersByProject = new Map<string, Set<string>>();
      for (const owner of owners) {
        if (!owner.user_id) continue;
        const name = userNameById.get(owner.user_id);
        if (!name) continue;
        const names = salesOwnersByProject.get(owner.project_id) ?? new Set<string>();
        names.add(name);
        salesOwnersByProject.set(owner.project_id, names);
      }

      const soldHoursMap = new Map<string, number>();
      for (const a of assignments) {
        const projectId = (a.budget_lines as { project_id: string } | null)?.project_id;
        if (!projectId) continue;
        soldHoursMap.set(projectId, (soldHoursMap.get(projectId) ?? 0) + Number(a.hours ?? 0));
      }

      const usedHoursMap = new Map<string, number>();
      for (const te of timeEntries) {
        if (!te.project_id) continue;
        usedHoursMap.set(
          te.project_id,
          (usedHoursMap.get(te.project_id) ?? 0) + (te.duration_seconds ?? 0) / 3600,
        );
      }

      const inputs: ProjectInput[] = projects.map((p) => ({
        id: p.id,
        name: p.name,
        projectCode: p.project_code,
        totalAmount: p.total_amount,
        bfActual: p.bf_actual,
        projectStatus: p.project_status,
        customerName: (p.customer as { name: string } | null)?.name ?? null,
        salesOwnerNames: [...(salesOwnersByProject.get(p.id) ?? [])].sort((a, b) => a.localeCompare(b, 'nb')),
        projectManagerName: p.production_lead_id ? userNameById.get(p.production_lead_id) ?? null : null,
      }));

      return {
        timeprisRanking: computeTimeprisRanking(inputs, soldHoursMap, usedHoursMap),
        bfRanking: computeBfRanking(inputs, soldHoursMap, usedHoursMap),
      };
    },
    staleTime: 5 * 60 * 1000,
  });
}
